import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Link
} from '@mui/material';
import { CheckCircle, Email, Error as ErrorIcon } from '@mui/icons-material';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAppNavigation } from '../hooks/useAppNavigation';
import authService from '../services/authService';

const VerifyEmail = () => {
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState(false);
  const [verificationComplete, setVerificationComplete] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  
  const navigate = useAppNavigation();
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  
  // Get token from URL params or search params
  const verificationToken = token || searchParams.get('token');

  // Define validateToken function before useEffect that calls it
  const validateToken = useCallback(async () => {
    console.log('🔵 [VALIDATE] Starting token validation for:', verificationToken);
    setValidating(true);
    setError('');

    try {
      console.log('🔵 [VALIDATE] Calling authService.validateVerificationToken...');
      const response = await authService.validateVerificationToken(verificationToken);
      console.log('🔵 [VALIDATE] Validation response:', response);
      
      if (response.success && response.data?.valid) {
        console.log('🟢 [VALIDATE] Token is valid, proceeding to verify...');
        setTokenValid(true);
        // Automatically verify the email
        handleVerify();
      } else {
        console.log('🔴 [VALIDATE] Token validation failed:', response.error);
        
        // Check if this is an "already verified" error - treat it as success
        if (response.error && response.error.includes('already been verified')) {
          console.log('🟡 [VALIDATE] Token validation failed because email is already verified - treating as success');
          
          // Store verification success message since user is already verified
          const successData = {
            timestamp: Date.now(),
            message: 'Your email has been verified successfully! You now have full access to all features.'
          };
          console.log('🟡 [VALIDATE] Storing success message in sessionStorage:', successData);
          sessionStorage.setItem('emailVerificationSuccess', JSON.stringify(successData));
          
          // Redirect to login immediately
          console.log('🟡 [VALIDATE] Redirecting to login page...');
          navigate.to.LOGIN();
        } else {
          setError(response.error || 'Invalid or expired verification token');
          setTokenValid(false);
        }
      }
    } catch (err) {
      console.error('🔴 [VALIDATE] Token validation error:', err);
      console.log('🔴 [VALIDATE] Error details - status:', err.response?.status, 'data:', err.response?.data);
      
      // Check if this is an "already verified" error - treat it as success
      if (err.response && err.response.status === 400 && 
          err.response.data?.error?.includes('already been verified')) {
        
        console.log('🟡 [VALIDATE] Token validation failed because email is already verified - treating as success');
        
        // Store verification success message since user is already verified
        const successData = {
          timestamp: Date.now(),
          message: 'Your email has been verified successfully! You now have full access to all features.'
        };
        console.log('🟡 [VALIDATE] Storing success message in sessionStorage:', successData);
        sessionStorage.setItem('emailVerificationSuccess', JSON.stringify(successData));
        
        // Redirect to login immediately
        console.log('🟡 [VALIDATE] Redirecting to login page...');
        navigate.to.LOGIN();
      } else {
        console.log('🔴 [VALIDATE] Unhandled validation error, showing error message');
        setError('Failed to validate verification token. Please try again.');
        setTokenValid(false);
      }
    } finally {
      console.log('🔵 [VALIDATE] Token validation completed');
      setValidating(false);
    }
  }, [verificationToken, navigate]);

  useEffect(() => {
    if (verificationToken) {
      validateToken();
    } else {
      setError('Invalid verification link. Please check your email for the correct link.');
      setValidating(false);
    }
  }, [verificationToken, validateToken]);

  const handleVerify = async () => {
    console.log('🔵 [VERIFY] Starting verification process for token:', verificationToken);
    setLoading(true);
    setError('');
    setMessage('');

    try {
      console.log('🔵 [VERIFY] Calling authService.verifyEmail...');
      const response = await authService.verifyEmail(verificationToken);
      console.log('🔵 [VERIFY] Response received:', response);
      
      if (response.success) {
        console.log('🟢 [VERIFY] SUCCESS: Verification succeeded!');
        setVerificationComplete(true);
        setMessage('Your email has been verified successfully! You can now access all features.');
        
        // Store verification success in sessionStorage for display after login
        const successData = {
          timestamp: Date.now(),
          message: 'Your email has been verified successfully! You now have full access to all features.'
        };
        console.log('🟢 [VERIFY] Storing success message in sessionStorage:', successData);
        sessionStorage.setItem('emailVerificationSuccess', JSON.stringify(successData));
        
        // Redirect to login after 3 seconds
        console.log('🟢 [VERIFY] Setting timeout to redirect to login in 3 seconds...');
        setTimeout(() => {
          console.log('🟢 [VERIFY] Redirecting to login page...');
          navigate.to.LOGIN();
        }, 3000);
      } else {
        console.log('🔴 [VERIFY] FAILED: Response not successful:', response.error);
        setError(response.error || 'Failed to verify email. Please try again.');
      }
    } catch (err) {
      console.error('🔴 [VERIFY] ERROR caught:', err);
      console.log('🔴 [VERIFY] Error details - status:', err.response?.status, 'data:', err.response?.data);
      
      // Check if this is a server error or already verified error
      if (err.response && (err.response.status === 500 || 
          (err.response.status === 400 && err.response.data?.error?.includes('already been verified')))) {
        
        if (err.response.status === 500) {
          console.log('🟡 [VERIFY] 500 ERROR: Server error during verification, but verification may have succeeded. Storing success message anyway.');
        } else {
          console.log('🟡 [VERIFY] 400 ERROR: Email already verified. Storing success message for user feedback.');
        }
        
        // Store verification success in sessionStorage for both cases
        // 500: verification likely succeeded despite error
        // 400 "already verified": user should see confirmation they're verified
        const successData = {
          timestamp: Date.now(),
          message: 'Your email has been verified successfully! You now have full access to all features.'
        };
        console.log('🟡 [VERIFY] Storing success message in sessionStorage despite error:', successData);
        sessionStorage.setItem('emailVerificationSuccess', JSON.stringify(successData));
        
        // Redirect to login immediately since there was an error
        console.log('🟡 [VERIFY] Redirecting to login immediately due to error...');
        navigate.to.LOGIN();
      } else {
        console.log('🔴 [VERIFY] UNHANDLED ERROR: Not a 400/500 error, showing error message');
        setError('Failed to verify email. Please try again.');
      }
    } finally {
      console.log('🔵 [VERIFY] Verification process completed, setting loading to false');
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    setResendMessage('');
    setError('');

    try {
      // Get user email from localStorage or prompt
      const userEmail = localStorage.getItem('userEmail');
      if (!userEmail) {
        setError('Unable to resend verification email. Please log in and request a new verification email.');
        setResendLoading(false);
        return;
      }

      const response = await authService.sendVerificationEmail(userEmail);
      
      if (response.success) {
        setResendMessage('Verification email sent! Please check your inbox.');
      } else {
        setError(response.error || 'Failed to send verification email.');
      }
    } catch (err) {
      console.error('Resend verification error:', err);
      setError('Failed to send verification email. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  if (validating) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6">
            Validating verification link...
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (verificationComplete) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Email Verified!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {message}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Redirecting to login...
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (error && !tokenValid) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom>
              Verification Failed
            </Typography>
          </Box>

          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Your verification link may have expired or is invalid.
            </Typography>
            
            <Button
              variant="contained"
              onClick={handleResendVerification}
              disabled={resendLoading}
              startIcon={resendLoading ? <CircularProgress size={20} /> : <Email />}
              sx={{ mb: 2 }}
            >
              {resendLoading ? 'Sending...' : 'Resend Verification Email'}
            </Button>

            {resendMessage && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {resendMessage}
              </Alert>
            )}

            <Typography variant="body2" sx={{ mt: 2 }}>
              <Link
                component="button"
                variant="body2"
                onClick={() => navigate.to.LOGIN()}
                sx={{ cursor: 'pointer' }}
              >
                Return to Login
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
        <Email sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom>
          Verifying Your Email
        </Typography>
        
        {loading && (
          <Box sx={{ my: 3 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Please wait while we verify your email...
            </Typography>
          </Box>
        )}

        {message && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {message}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>
    </Container>
  );
};

export default VerifyEmail;
